import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { subscriptionsAPI } from '../lib/api/subscriptions';
import { useAuth } from './useAuth';

// Cache for reactivation status - shared across all hook instances
const reactivationCache = {
  data: null,
  timestamp: null,
  CACHE_DURATION: 5 * 60 * 1000, // 5 minutes
  isValid() {
    return this.data !== null && 
           this.timestamp && 
           (Date.now() - this.timestamp) < this.CACHE_DURATION;
  },
  set(data) {
    this.data = data;
    this.timestamp = Date.now();
  },
  clear() {
    this.data = null;
    this.timestamp = null;
  }
};

export const useReactivationGuard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const hasChecked = useRef(false);

  useEffect(() => {
    const checkReactivationStatus = async () => {
      // Only check for admins
      if (!user || user.role !== 'admin') {
        setChecking(false);
        return;
      }

      // If user already flagged as needing reactivation, redirect immediately
      if (user.needsReactivation) {
        navigate('/reactivation', { replace: true });
        return;
      }

      // If we've already checked in this session and have valid cache, use it
      if (hasChecked.current && reactivationCache.isValid()) {
        if (reactivationCache.data?.needsReactivation) {
          navigate('/reactivation', { replace: true });
          return;
        }
        setChecking(false);
        return;
      }

      try {
        const response = await subscriptionsAPI.getReactivationStatus();
        
        // Cache the response
        reactivationCache.set(response);
        hasChecked.current = true;
        
        if (response.needsReactivation) {
          navigate('/reactivation', { replace: true });
          return;
        }
      } catch (error) {
        console.error('Failed to check reactivation status:', error);
        // If API call fails, don't block the user - let them continue
        // The reactivation page will handle the error display
        hasChecked.current = true; // Don't retry on every navigation
      }
      
      setChecking(false);
    };

    checkReactivationStatus();
  }, [user?.id, navigate]); // Only re-run if user ID changes, not on every navigation

  return { checking };
};
